import React, { useState } from 'react';
import { PurchaseOrder, Vendor, Product, UnitType } from '../types';
import { Plus, Search, Truck, CheckCircle, XCircle, Clock, ChevronDown, Calendar, Package, X, Hash, Layers, Box, Pill, DollarSign, RefreshCw } from 'lucide-react';

interface ReceiveUpdate {
  batch: string;
  expiry: string;
  quantity: number;
  pricePack: number;
  priceStrip: number;
  priceUnit: number;
  autoUpdatePricing: boolean;
}

interface PurchaseOrdersProps {
  orders: PurchaseOrder[];
  vendors: Vendor[];
  products: Product[];
  onCreateOrder: (order: Omit<PurchaseOrder, 'id'>) => void;
  onReceiveOrder: (orderId: string, updates: { [key: string]: ReceiveUpdate }) => void;
  onCancelOrder: (orderId: string) => void;
}

const PurchaseOrders: React.FC<PurchaseOrdersProps> = ({ 
  orders, vendors, products, onCreateOrder, onReceiveOrder, onCancelOrder 
}) => {
  const [view, setView] = useState<'LIST' | 'CREATE'>('LIST');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVendorId, setSelectedVendorId] = useState('');
  const [orderItems, setOrderItems] = useState<{productId: string; productName: string; quantity: number; cost: number; unit: UnitType}[]>([]);
  const [itemSearch, setItemSearch] = useState('');
  const [receivingOrder, setReceivingOrder] = useState<PurchaseOrder | null>(null);
  const [receiveFormData, setReceiveFormData] = useState<{[key: string]: ReceiveUpdate}>({});

  const filteredOrders = orders.filter(o => o.vendorName.toLowerCase().includes(searchTerm.toLowerCase()) || o.id.toLowerCase().includes(searchTerm.toLowerCase()));
  const filteredProducts = products.filter(p => p.name.toLowerCase().includes(itemSearch.toLowerCase()));

  const handleAddItem = (product: Product) => {
    const isTablet = product.category === 'Tablets';
    const unit: UnitType = isTablet ? 'Pack' : 'Unit';
    
    const existing = orderItems.find(i => i.productId === product.id && i.unit === unit);
    if (existing) {
      setOrderItems(prev => prev.map(i => (i.productId === product.id && i.unit === unit) ? {...i, quantity: i.quantity + 1} : i));
    } else {
      setOrderItems([...orderItems, { 
        productId: product.id, productName: product.name, quantity: 1, 
        cost: isTablet ? (product.pricePack || product.cost * product.stripsPerPack * product.unitsPerStrip) : product.cost,
        unit: unit
      }]);
    }
  };

  const handleInitiateReceive = (order: PurchaseOrder) => {
    setReceivingOrder(order);
    const initialData: {[key: string]: ReceiveUpdate} = {};
    order.items.forEach(item => {
        const product = products.find(p => p.id === item.productId);
        let unitsToReceive = item.quantity;
        if (product) {
          if (item.unit === 'Pack') unitsToReceive *= (product.stripsPerPack * product.unitsPerStrip);
          if (item.unit === 'Strip') unitsToReceive *= product.unitsPerStrip;
        }
        initialData[item.productId] = {
            batch: product?.batchNumber || '',
            expiry: product?.expiryDate || new Date().toISOString().split('T')[0],
            quantity: unitsToReceive,
            pricePack: product?.pricePack || 0,
            priceStrip: product?.priceStrip || 0,
            priceUnit: product?.priceUnit || 0,
            autoUpdatePricing: product?.autoUpdatePricing ?? true
        };
    });
    setReceiveFormData(initialData);
  };

  const handleReceiveInputChange = (productId: string, field: keyof ReceiveUpdate, value: any) => {
    setReceiveFormData(prev => {
      const current = prev[productId];
      const product = products.find(p => p.id === productId);
      const isTablet = product?.category === 'Tablets';
      let nextUpdate = { ...current, [field]: value };

      if (isTablet) {
        if (nextUpdate.autoUpdatePricing && (field === 'pricePack' || field === 'autoUpdatePricing')) {
          const strips = Math.max(1, product?.stripsPerPack || 1);
          const units = Math.max(1, product?.unitsPerStrip || 1);
          nextUpdate.priceStrip = parseFloat((nextUpdate.pricePack / strips).toFixed(2));
          nextUpdate.priceUnit = parseFloat((nextUpdate.priceStrip / units).toFixed(2));
        }
      } else {
        // Non-tablets single unit sync
        if (field === 'pricePack') {
          nextUpdate.priceStrip = value;
          nextUpdate.priceUnit = value;
        } else if (field === 'priceUnit') {
          nextUpdate.pricePack = value;
          nextUpdate.priceStrip = value;
        }
      }
      return { ...prev, [productId]: nextUpdate };
    });
  };

  const handleConfirmReceive = () => {
    if (!receivingOrder) return;
    onReceiveOrder(receivingOrder.id, receiveFormData);
    setReceivingOrder(null);
  };

  const handleSubmitOrder = () => {
    if (!selectedVendorId || orderItems.length === 0) return;
    const vendor = vendors.find(v => v.id === selectedVendorId);
    if (!vendor) return;

    const totalCost = orderItems.reduce((sum, item) => sum + (item.cost * item.quantity), 0);
    const newOrder: Omit<PurchaseOrder, 'id'> = {
      vendorId: selectedVendorId,
      vendorName: vendor.name,
      date: new Date().toISOString().split('T')[0],
      status: 'Pending',
      totalCost: totalCost,
      items: orderItems
    };

    onCreateOrder(newOrder);
    setOrderItems([]);
    setSelectedVendorId('');
    setView('LIST');
  };

  if (view === 'CREATE') {
    const totalOrderCost = orderItems.reduce((sum, item) => sum + (item.cost * item.quantity), 0);
    return (
      <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-bold text-slate-800">New Purchase Order</h2>
          <button onClick={() => setView('LIST')} className="px-4 py-2 border rounded-lg hover:bg-slate-50 text-sm font-medium">Cancel</button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white p-6 rounded-xl border">
              <label className="block text-sm font-medium mb-2">Select Vendor</label>
              <select className="w-full p-3 border rounded-lg" value={selectedVendorId} onChange={e => setSelectedVendorId(e.target.value)}>
                <option value="">-- Choose Vendor --</option>
                {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
            {selectedVendorId && (
              <div className="bg-white p-6 rounded-xl border flex flex-col h-[500px]">
                <div className="relative mb-4"><Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} /><input type="text" placeholder="Search products..." value={itemSearch} onChange={e => setItemSearch(e.target.value)} className="w-full pl-10 pr-4 py-2 border rounded-lg" /></div>
                <div className="flex-1 overflow-y-auto border rounded-lg">
                   <table className="w-full text-left text-sm">
                     <thead className="bg-slate-50"><tr><th className="p-3">Product</th><th className="p-3 text-right">Category</th><th className="p-3 text-right">Action</th></tr></thead>
                     <tbody>{filteredProducts.map(p => <tr key={p.id} className="border-t">
                        <td className="p-3 font-medium">{p.name}</td>
                        <td className="p-3 text-right text-slate-500 font-bold text-xs uppercase">{p.category}</td>
                        <td className="p-3 text-right"><button onClick={() => handleAddItem(p)} className="text-emerald-600 font-bold bg-emerald-50 px-3 py-1 rounded-full text-xs">Add {p.category === 'Tablets' ? 'Pack' : 'Unit'}</button></td>
                     </tr>)}</tbody>
                   </table>
                </div>
              </div>
            )}
          </div>
          <div className="bg-white p-6 rounded-xl border h-fit sticky top-20">
             <h3 className="font-bold mb-4 flex items-center gap-2"><Truck size={20} className="text-emerald-600"/> Summary</h3>
             {orderItems.length === 0 ? <p className="text-center text-slate-400 py-8">No items added</p> : (
               <div className="space-y-4">
                 <div className="max-h-[350px] overflow-y-auto space-y-3">
                   {orderItems.map((item, idx) => {
                     const isTablet = products.find(p => p.id === item.productId)?.category === 'Tablets';
                     return <div key={idx} className="p-3 bg-slate-50 border rounded-lg text-sm">
                       <div className="flex justify-between font-bold mb-2"><span>{item.productName}</span><button onClick={() => setOrderItems(orderItems.filter((_, i) => i !== idx))}><XCircle size={14} className="text-slate-400 hover:text-red-500"/></button></div>
                       <div className="grid grid-cols-2 gap-2 mb-2">
                         <select disabled={!isTablet} className="p-1 border rounded bg-white text-xs disabled:opacity-50" value={item.unit} onChange={e => {const ni = [...orderItems]; ni[idx].unit = e.target.value as any; setOrderItems(ni);}}><option value="Pack">Pack</option><option value="Strip">Strip</option><option value="Unit">Unit</option></select>
                         <input type="number" className="p-1 border rounded text-xs" value={item.quantity} onChange={e => {const ni = [...orderItems]; ni[idx].quantity = parseInt(e.target.value) || 0; setOrderItems(ni);}} />
                       </div>
                       <div className="space-y-1">
                         <label className="text-[10px] font-bold text-slate-400 uppercase">Cost per {item.unit}</label>
                         <input type="number" step="0.01" className="w-full p-1 border rounded text-xs font-bold text-red-600" value={item.cost} onChange={e => {const ni = [...orderItems]; ni[idx].cost = parseFloat(e.target.value) || 0; setOrderItems(ni);}} />
                       </div>
                     </div>
                   })}
                 </div>
                 <div className="pt-4 border-t"><div className="flex justify-between font-bold text-lg mb-4 text-red-600"><span>Total</span><span>RS. {totalOrderCost.toFixed(2)}</span></div><button onClick={handleSubmitOrder} className="w-full py-3 bg-emerald-600 text-white rounded-lg font-bold hover:bg-emerald-700 transition-all shadow-md">Confirm Order</button></div>
               </div>
             )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-lg font-bold">Purchase Orders</h2>
          <button onClick={() => setView('CREATE')} className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 hover:bg-emerald-700 transition-all"><Plus size={18}/> New Order</button>
        </div>
        <table className="w-full text-left">
          <thead className="bg-slate-50 text-[10px] font-bold uppercase text-slate-400 tracking-wider"><tr className="border-b"><th className="p-4">Order ID</th><th className="p-4">Date</th><th className="p-4">Vendor</th><th className="p-4">Total Cost</th><th className="p-4">Status</th><th className="p-4 text-right">Actions</th></tr></thead>
          <tbody className="divide-y divide-slate-100">{filteredOrders.map(order => <tr key={order.id} className="text-sm">
            <td className="p-4 font-mono text-slate-500 text-xs">{order.id}</td><td className="p-4">{order.date}</td><td className="p-4 font-bold">{order.vendorName}</td><td className="p-4 font-bold text-red-600">RS. {order.totalCost.toFixed(2)}</td>
            <td className="p-4"><span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${order.status === 'Received' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{order.status}</span></td>
            <td className="p-4 text-right">{order.status === 'Pending' && <button onClick={() => handleInitiateReceive(order)} className="bg-emerald-600 text-white px-3 py-1.5 rounded font-bold text-xs hover:bg-emerald-700 transition-all">Receive Stock</button>}</td>
          </tr>)}</tbody>
        </table>
      </div>

      {receivingOrder && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
           <div className="bg-white w-full max-w-5xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in fade-in zoom-in duration-200">
              <div className="p-5 border-b bg-slate-50 flex justify-between items-center"><div className="flex flex-col"><h3 className="font-bold text-lg text-slate-800">Receive Stock - #{receivingOrder.id}</h3><p className="text-xs text-slate-500">{receivingOrder.vendorName}</p></div><button onClick={() => setReceivingOrder(null)} className="p-2 hover:bg-slate-200 rounded-full transition-all text-slate-400"><X size={20} /></button></div>
              <div className="flex-1 overflow-y-auto p-6"><table className="w-full text-left border-collapse"><thead className="text-[10px] font-bold text-slate-400 uppercase tracking-widest"><tr><th className="p-3 border-b pb-4">Product Name</th><th className="p-3 border-b pb-4">Units to Add</th><th className="p-3 border-b pb-4">Batch & Expiry</th><th className="p-3 border-b pb-4 text-emerald-700">Selling Price Adjustment</th></tr></thead>
                <tbody className="divide-y divide-slate-100">{receivingOrder.items.map(item => {
                  const product = products.find(p => p.id === item.productId);
                  const isTablet = product?.category === 'Tablets';
                  return <tr key={item.productId} className="align-top hover:bg-slate-50/50">
                    <td className="p-3 pt-5"><div className="font-bold text-slate-900">{item.productName}</div><div className="text-[10px] text-slate-400 uppercase font-black">{product?.category}</div></td>
                    <td className="p-3 pt-5"><input type="number" className="w-full border border-slate-200 p-2 rounded-lg text-sm font-bold" value={receiveFormData[item.productId]?.quantity || 0} onChange={e => handleReceiveInputChange(item.productId, 'quantity', parseInt(e.target.value) || 0)} /><p className="text-[10px] text-slate-400 mt-1">Ordered: {item.quantity} {item.unit}</p></td>
                    <td className="p-3 pt-5 space-y-2"><input type="text" placeholder="Batch Number" className="w-full border border-slate-200 p-2 rounded-lg text-xs font-mono" value={receiveFormData[item.productId]?.batch || ''} onChange={e => handleReceiveInputChange(item.productId, 'batch', e.target.value)} /><input type="date" className="w-full border border-slate-200 p-2 rounded-lg text-xs" value={receiveFormData[item.productId]?.expiry || ''} onChange={e => handleReceiveInputChange(item.productId, 'expiry', e.target.value)} /></td>
                    <td className="p-3 pt-5 bg-emerald-50/20">
                      {isTablet && <div className="flex items-center gap-2 mb-3 bg-white p-1.5 rounded-lg border border-emerald-100"><input type="checkbox" id={`auto-${item.productId}`} checked={receiveFormData[item.productId]?.autoUpdatePricing} onChange={e => handleReceiveInputChange(item.productId, 'autoUpdatePricing', e.target.checked)} className="w-3.5 h-3.5 text-emerald-600 rounded" /><label htmlFor={`auto-${item.productId}`} className="text-[10px] font-black uppercase text-emerald-700 flex items-center gap-1 cursor-pointer"><RefreshCw size={10} className={receiveFormData[item.productId]?.autoUpdatePricing ? 'animate-spin-slow' : ''} /> Auto-Sync Strips/Units</label></div>}
                      <div className="space-y-2">
                        {isTablet ? (
                          <>
                            <div className="flex items-center gap-2"><span className="text-[10px] font-bold w-12 text-slate-500 uppercase">Pack RS.</span><input type="number" step="0.01" className="flex-1 border border-emerald-200 p-2 rounded-lg font-bold text-emerald-600 focus:ring-2 focus:ring-emerald-500 outline-none" value={receiveFormData[item.productId]?.pricePack || 0} onChange={e => handleReceiveInputChange(item.productId, 'pricePack', parseFloat(e.target.value) || 0)} /></div>
                            <div className="flex items-center gap-2"><span className="text-[10px] font-bold w-12 text-slate-500 uppercase">Strip RS.</span><input type="number" step="0.01" disabled={receiveFormData[item.productId]?.autoUpdatePricing} className="flex-1 border border-slate-200 p-2 rounded-lg font-bold text-emerald-600 disabled:bg-slate-50" value={receiveFormData[item.productId]?.priceStrip || 0} onChange={e => handleReceiveInputChange(item.productId, 'priceStrip', parseFloat(e.target.value) || 0)} /></div>
                            <div className="flex items-center gap-2"><span className="text-[10px] font-bold w-12 text-slate-500 uppercase">Unit RS.</span><input type="number" step="0.01" disabled={receiveFormData[item.productId]?.autoUpdatePricing} className="flex-1 border border-slate-200 p-2 rounded-lg font-bold text-emerald-600 disabled:bg-slate-50" value={receiveFormData[item.productId]?.priceUnit || 0} onChange={e => handleReceiveInputChange(item.productId, 'priceUnit', parseFloat(e.target.value) || 0)} /></div>
                          </>
                        ) : (
                          <div className="flex items-center gap-2"><span className="text-[10px] font-bold w-16 text-slate-500 uppercase">Sell RS.</span><input type="number" step="0.01" className="flex-1 border border-emerald-200 p-2 rounded-lg font-bold text-emerald-600 focus:ring-2 focus:ring-emerald-500 outline-none" value={receiveFormData[item.productId]?.priceUnit || 0} onChange={e => handleReceiveInputChange(item.productId, 'priceUnit', parseFloat(e.target.value) || 0)} /></div>
                        )}
                      </div>
                    </td>
                  </tr>;
                })}</tbody></table></div>
              <div className="p-4 border-t bg-slate-50 flex justify-end gap-3"><button onClick={() => setReceivingOrder(null)} className="px-6 py-2.5 border border-slate-300 rounded-xl font-bold text-sm text-slate-700 hover:bg-white transition-all">Cancel</button><button onClick={handleConfirmReceive} className="px-8 py-2.5 bg-emerald-600 text-white rounded-xl font-bold text-sm shadow-lg hover:bg-emerald-700 transition-all flex items-center gap-2"><CheckCircle size={18} /> Complete Receiving</button></div>
           </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrders;